import * as React from 'react';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';
import groupBy from 'lodash/groupBy';

import { withRouter } from 'libs/router';

import ActionButton from 'components/buttons/action-button/ActionButton';
import Spinner from 'components/spinner/Spinner';
import CheckboxesList from 'components/inputs/checkboxes-list/CheckboxesList';
import { ModalFrameFooter, ModalFrameMessage, ModalFrameSeparator } from 'components/modal';
import BrandTabsModal from 'components/brands-filter/BrandTabsModal';
import EntityResources from 'components/entity/entity-resources/EntityResources';
import withInfluencerMeta from 'components/brands-filter/WithInfluencerMeta';

import {
  addToShortlists,
  fetchShortlists,
  removeFromShortlists,
  resetShortlists
} from 'modules/shortlist/reducers/shortlists';
import { fetchInfluencersMeta } from 'modules/influencers/reducers/influencersMeta';

import { getShortlistsForInfluencer } from 'utils/influencers';
import { BRAND } from 'utils/brands';
import { __t, applyTranslations } from 'utils/language';

import type {
  Campaign,
  CheckboxesListItem,
  InfluencerMeta,
  InfluencerProfile,
  Organization
} from 'types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('./add-to-shortlist-modal.i18n.json'));

type OwnProps = {
  influencerProfile: InfluencerProfile,
  influencerMeta: InfluencerMeta,
  onCloseModal: () => void
};

type StateProps = {
  shortlists: {
    isLoading: boolean,
    list: Array<Campaign>
  },
  activeBrandFilter: string,
  organizations: Array<Organization>
};

type DispatchProps = {
  dispatchFetchShortlists: (a: string) => Promise<void>,
  dispatchResetShortlists: () => Promise<void>,
  dispatchAddToShortlist: (b: number, a: CheckboxesListItem) => Promise<void>,
  dispatchRemoveFromShortlists: (a: Partial<CheckboxesListItem>) => Promise<void>,
  dispatchUpdateInfluencerMeta: (a: number) => Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  shortlists: Array<CheckboxesListItem>,
  ongoingShortlists: Array<CheckboxesListItem>
};


class AddToShortlistModal extends React.PureComponent<Props, State> {
  state = {
    shortlists: [],
    ongoingShortlists: []
  };

  componentDidMount() {
    this.fetchShortlists();
  }

  componentDidUpdate(prevProps) {
    const { activeBrandFilter, shortlists } = this.props;

    if (activeBrandFilter !== prevProps.activeBrandFilter) {
      this.fetchShortlists();
    }

    if (shortlists !== prevProps.shortlists) {
      this.groupShortlists();
    }
  }

  componentWillUnmount() {
    const { dispatchResetShortlists } = this.props;

    dispatchResetShortlists();
  }

  updateInfluencerMeta = () => {
    const { dispatchUpdateInfluencerMeta, influencerProfile } = this.props;
    dispatchUpdateInfluencerMeta(influencerProfile.id);
  };

  fetchShortlists() {
    const { activeBrandFilter, dispatchFetchShortlists } = this.props;

    dispatchFetchShortlists(activeBrandFilter);
  }

  groupShortlists() {
    const { influencerProfile, shortlists, organizations } = this.props;
    const influencerShortlists = getShortlistsForInfluencer(shortlists.list, influencerProfile.id, organizations);

    const nextState = influencerShortlists.reduce((a, c) => {
      if (c.isDisabled && c.isSelected) {
        a.ongoingShortlists.push(c);
      } else {
        a.shortlists.push(c);
      }

      return a;
    }, { shortlists: [], ongoingShortlists: [] } as { shortlists: Array<CheckboxesListItem>, ongoingShortlists: Array<CheckboxesListItem> });

    this.setState(nextState);
  }

  handleAdd = (item: CheckboxesListItem): void => {
    const { influencerProfile, dispatchAddToShortlist } = this.props;

    if (influencerProfile) {
      dispatchAddToShortlist(influencerProfile.id, item)
        .then(this.updateInfluencerMeta);
    }
  };

  handleRemove = (item: CheckboxesListItem): void => {
    const { dispatchRemoveFromShortlists } = this.props;

    dispatchRemoveFromShortlists(item)
      .then(this.updateInfluencerMeta);
  };

  handleListItemClick = (event: SyntheticEvent<HTMLElement>, item: CheckboxesListItem): void => {
    event.stopPropagation();
    event.preventDefault();

    if (item.isDisabled) {
      return;
    }

    if (!item.isSelected) {
      this.handleAdd(item);
    } else {
      this.handleRemove(item);
    }
  };

  renderByBrand(shortlists: Array<CheckboxesListItem>, otherProps: any): React.ReactNode {
    const { activeBrandFilter } = this.props;
    const groupedByBrandById: {
      [x: string]: Array<CheckboxesListItem>
    } = groupBy(shortlists, 'entity.id');
    // $FlowIgnore
    const arrayOfShortlists: Array<Array<CheckboxesListItem>> = Object.values(groupedByBrandById);
    const groupedByBrand: Array<Array<CheckboxesListItem>> = sortBy(arrayOfShortlists,
      brandShortlists => (brandShortlists[0].entity && brandShortlists[0].entity.displayName || '').toLowerCase()
    );

    return (
      <React.Fragment>
        {
          groupedByBrand.map((brandShortlists, index) => {
            const entity = brandShortlists[0].entity;

            return (
              <EntityResources
                withPaddedChildren
                key={`entity-resource-${String(entity ? entity.id : index)}`}
                entity={activeBrandFilter === BRAND ? null : entity}
              >
                <CheckboxesList
                  lists={brandShortlists}
                  {...otherProps}
                />
              </EntityResources>
            );
          })
        }
      </React.Fragment>
    );
  }

  renderCampaigns(): React.ReactNode {
    const { onCloseModal } = this.props;
    const { shortlists, ongoingShortlists } = this.state;
    const hasLists = !isEmpty(shortlists) || !isEmpty(ongoingShortlists);
    return (
      <div>
        {
          hasLists && (
            <React.Fragment>
              <ModalFrameMessage
                size="h3"
                message={`<strong>${__t('add_to_shortlist_modal.modal_shortlist_checkboxes_title')}</strong>`}
              />
              {
                shortlists.length === 0 &&
                  <ModalFrameMessage
                    message={__t('add_to_shortlist_modal.shortlist_actions_empty_list')}
                  />
              }
              {this.renderByBrand(shortlists, {
                onCheckboxChange: this.handleListItemClick
              })}
              {
                ongoingShortlists.length > 0 && (
                  <React.Fragment>
                    <ModalFrameSeparator />
                    <ModalFrameMessage
                      size="h3"
                      message={`<strong>${__t('add_to_shortlist_modal.modal_shortlist_ongoing_campaigns_title')}</strong>`}
                    />
                    {this.renderByBrand(ongoingShortlists, {
                      disabled: true
                    })}
                  </React.Fragment>
                )
              }
              <ModalFrameFooter>
                <ActionButton
                  onClick={onCloseModal}
                  data={{ qa: 'add-to-shortlists-modal__done-button' }}
                >
                  {__t('add_to_shortlist_modal.action_button_done')}
                </ActionButton>
              </ModalFrameFooter>
            </React.Fragment>
          )
        }
        {
          !hasLists && (
            <React.Fragment>
              <ModalFrameMessage
                message={__t('add_to_shortlist_modal.modal_no_shortlists_available')}
              />
              <ModalFrameFooter>
                <ActionButton
                  onClick={onCloseModal}
                >
                  {__t('add_to_shortlist_modal.action_button_done')}
                </ActionButton>
              </ModalFrameFooter>
            </React.Fragment>
          )
        }
      </div>
    );
  }

  render(): React.ReactNode {
    const { influencerProfile, shortlists, influencerMeta } = this.props;

    return (
      <BrandTabsModal
        title={__t('add_to_shortlist_modal.modal_shortlist_title')}
        dataProps={{ 'data-qa': 'add-to-shortlists-modal' }}
        influencerProfile={influencerProfile}
        influencerMeta={influencerMeta}
        metaKey="campaigns"
      >
        {
          shortlists.isLoading ?
            <Spinner
              position="inline"
              size="small"
              color="light"
            /> :
            this.renderCampaigns()
        }
      </BrandTabsModal>
    );
  }
}

const mapStateToProps = ({ shortlists, organizations, brandsFilter }): StateProps => ({
  shortlists,
  organizations: organizations.list,
  activeBrandFilter: brandsFilter.activeFilter
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchShortlists: (activeBrandFilter) => dispatch(fetchShortlists(activeBrandFilter)),
  dispatchResetShortlists: () => dispatch(resetShortlists()),
  dispatchAddToShortlist: (influencerId, item) => dispatch(addToShortlists(influencerId, item)),
  dispatchRemoveFromShortlists: (item) => dispatch(removeFromShortlists(item)),
  dispatchUpdateInfluencerMeta: (influencerId) => dispatch(fetchInfluencersMeta([influencerId]))
});

const EnhancedAddToShortlistModal = withInfluencerMeta()(AddToShortlistModal);
export default connect(mapStateToProps, mapDispatchToProps)(withRouter(EnhancedAddToShortlistModal));
